import React, { useState, useRef, forwardRef } from "react";
import { Select, MenuItem, Box, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

interface CustomSelectProps {
  labelId: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string; badge?: string }[];
  width?: number | string;
  placeholder?: string;
  showPlaceholderIcon?: boolean;
  placeholderIcon?: React.ReactNode;
  /** Per-instance overrides merged LAST into the root sx. Opt-in only —
   *  shared consumers render exactly as before when omitted. */
  sx?: object;
}

// Custom icon component that forwards refs and doesn't block clicks
const CustomIcon = forwardRef<HTMLDivElement, { open: boolean }>((props, ref) => (
  <Box
    ref={ref}
    {...props}
    sx={{
      transform: props.open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.3s ease",
      marginRight: 0.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 24,
      height: 24,
      top: '9px !important',
    }}
  >
    <KeyboardArrowDownIcon sx={{ color: "var(--text-2)" }} />
  </Box>
));

const CustomSelect: React.FC<CustomSelectProps> = ({
  labelId,
  name,
  value,
  onChange,
  options,
  width = 200,
  placeholder = "Select option",
  showPlaceholderIcon = false,
  placeholderIcon,
  sx: sxOverrides,
}) => {
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | undefined>();
  const selectRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    if (selectRef.current) {
      setMenuWidth(selectRef.current.clientWidth);
    }
    setOpen(true);
  };

  return (
    <Select
      ref={selectRef}
      labelId={labelId}
      size="small"
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      displayEmpty
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 300,
            ...(menuWidth ? { width: menuWidth } : {}),
          },
        },
      }}
      IconComponent={(props) => <CustomIcon {...props} open={open} />}
      renderValue={(selected) =>
        selected === "" ? (
          <Box sx={{ display: "flex", alignItems: "center", color: "var(--text-2)" }}>
            {showPlaceholderIcon && (
              <Box sx={{ display: "flex", alignItems: "center", mr: "6px" }}>
                {placeholderIcon || (
                  <InsertDriveFileOutlinedIcon fontSize="small" />
                )}
              </Box>
            )}
            <Typography fontSize={14}>{placeholder}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", color: "var(--text-2)", minWidth: 0, width: "100%", overflow: "hidden" }}>
            {showPlaceholderIcon && (
              <Box sx={{ display: "flex", alignItems: "center", mr: "6px", flexShrink: 0 }}>
                {placeholderIcon || (
                  <InsertDriveFileOutlinedIcon fontSize="small" />
                )}
              </Box>
            )}
            <Typography
              fontSize={14}
              noWrap
            >
              {options.find((opt) => opt.value == selected)?.label || placeholder}
            </Typography>
          </Box>
        )
      }
      sx={{
        width,
        maxWidth: "100%",
        overflow: "hidden",
        borderRadius: "12px",
        bgcolor: "var(--surface)",
        color: "var(--text-2)",
        "& .MuiSelect-select": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        // --border is a hairline-RULE token (rgba(255,255,255,.08) in dark,
        // ~1.15:1 on --surface), so the RESTING outline of this control was
        // invisible until hovered. Light is untouched; dark shifts the whole
        // scale up one step so resting is visible AND hover still escalates.
        "& .MuiOutlinedInput-notchedOutline": {
          border: "1px solid var(--border)",
        },
        '[data-theme="dark"] & .MuiOutlinedInput-notchedOutline': {
          borderColor: "var(--muted)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: "1px solid var(--muted)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "1px solid var(--muted)",
        },
        '[data-theme="dark"] &:hover .MuiOutlinedInput-notchedOutline, [data-theme="dark"] &.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: "var(--text-2)",
          },
        ...(sxOverrides || {}),
      }}
    >
      <MenuItem value="" disabled>
        {placeholder}
      </MenuItem>

      {options.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          sx={{ color: "var(--text-2)", whiteSpace: "normal", wordBreak: "break-word", display: "flex", alignItems: "center", gap: 1 }}
        >
          {option.badge && (
            <span
              style={{
                backgroundColor: "var(--accent)",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 700,
                borderRadius: "6px",
                padding: "2px 8px",
                flexShrink: 0,
              }}
            >
              {option.badge}
            </span>
          )}
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export default CustomSelect;
